import React, { useState, useEffect, useCallback } from 'react';
import {
    collection, query, where, getDocs, doc, getDoc, orderBy, limit,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/config';
import {
    ShieldCheck, Search, CheckCircle2, XCircle, AlertTriangle,
    Loader, Building2, MapPin, Phone, User, Hash, RefreshCw,
} from 'lucide-react';

/**
 * 치과 클레임 통합 검수 페이지
 * 3개 탭:
 *   1. 검증 대기 — pending_verification (가입 시 자동 매칭, 14일 기한)
 *   2. 신규 요청 — hospitalClaimRequests (HIRA에 없어서 사용자가 신규 요청)
 *   3. 매칭 분쟁 — claimDisputes (잘못 매칭됐다고 신고)
 */
function AdminHospitalClaimReview() {
    const [tab, setTab] = useState('pending');
    const [pending, setPending] = useState([]);
    const [requests, setRequests] = useState([]);
    const [disputes, setDisputes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            if (tab === 'pending') {
                const snap = await getDocs(query(
                    collection(db, 'hospitals'),
                    where('matchStatus', '==', 'pending_verification'),
                    limit(50)
                ));
                const items = await Promise.all(snap.docs.map(async (d) => {
                    const h = { id: d.id, ...d.data() };
                    const u = h.claimedBy ? await getUser(h.claimedBy) : null;
                    return { ...h, user: u };
                }));
                setPending(items);
            } else if (tab === 'requests') {
                const snap = await getDocs(query(
                    collection(db, 'hospitalClaimRequests'),
                    where('status', '==', 'pending'),
                    orderBy('createdAt', 'desc'),
                    limit(50)
                ));
                const items = await Promise.all(snap.docs.map(async (d) => {
                    const r = { id: d.id, ...d.data() };
                    const u = r.requestedBy ? await getUser(r.requestedBy) : null;
                    return { ...r, user: u };
                }));
                setRequests(items);
            } else if (tab === 'disputes') {
                const snap = await getDocs(query(
                    collection(db, 'claimDisputes'),
                    where('status', '==', 'pending'),
                    orderBy('createdAt', 'desc'),
                    limit(50)
                ));
                const items = await Promise.all(snap.docs.map(async (d) => {
                    const r = { id: d.id, ...d.data() };
                    const u = r.reportedBy ? await getUser(r.reportedBy) : null;
                    const h = r.hospitalId ? await getHospital(r.hospitalId) : null;
                    return { ...r, user: u, hospital: h };
                }));
                setDisputes(items);
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [tab]);

    useEffect(() => { load(); }, [load]);

    const getUser = async (uid) => {
        try {
            const snap = await getDoc(doc(db, 'users', uid));
            return snap.exists() ? { id: snap.id, ...snap.data() } : null;
        } catch { return null; }
    };
    const getHospital = async (id) => {
        try {
            const snap = await getDoc(doc(db, 'hospitals', id));
            return snap.exists() ? { id: snap.id, ...snap.data() } : null;
        } catch { return null; }
    };

    return (
        <div style={S.page}>
            <div style={S.header}>
                <h1 style={S.title}>
                    <ShieldCheck size={22} style={{ verticalAlign: 'middle', marginRight: 8 }} />
                    치과 클레임 검수
                </h1>
                <p style={S.subtitle}>
                    회원가입 후 매칭된 치과를 관리자가 직접 검수·승인합니다.
                </p>
            </div>

            <div style={S.tabs}>
                <TabBtn active={tab === 'pending'} onClick={() => setTab('pending')}>
                    검증 대기 ({pending.length})
                </TabBtn>
                <TabBtn active={tab === 'requests'} onClick={() => setTab('requests')}>
                    신규 요청 ({requests.length})
                </TabBtn>
                <TabBtn active={tab === 'disputes'} onClick={() => setTab('disputes')}>
                    매칭 분쟁 ({disputes.length})
                </TabBtn>
                <button onClick={load} style={S.refreshBtn} disabled={loading}>
                    {loading ? <Loader size={12} /> : <RefreshCw size={12} />} 새로고침
                </button>
            </div>

            {error && (
                <div style={S.error}>
                    <AlertTriangle size={14} /> {error}
                </div>
            )}

            {loading ? (
                <div style={S.empty}><Loader size={20} /> 로딩 중…</div>
            ) : (
                <>
                    {tab === 'pending' && <PendingTab items={pending} onDone={load} />}
                    {tab === 'requests' && <RequestsTab items={requests} onDone={load} />}
                    {tab === 'disputes' && <DisputesTab items={disputes} onDone={load} />}
                </>
            )}
        </div>
    );
}

/* ============== Tab: 검증 대기 ============== */
function PendingTab({ items, onDone }) {
    const [busy, setBusy] = useState(null);
    const fns = getFunctions(undefined, 'asia-northeast3');

    const approve = async (h) => {
        if (!window.confirm(`${h.name} 의 매칭을 승인할까요?\n신청자: ${h.user?.email}`)) return;
        setBusy(h.id);
        try {
            await httpsCallable(fns, 'adminApproveClaim')({ hospitalId: h.id, expectedClaimedBy: h.claimedBy });
            onDone();
        } catch (e) { alert(e.message); }
        setBusy(null);
    };
    const reject = async (h) => {
        const reason = window.prompt(`${h.name} 매칭을 거부합니다. 사유를 입력하세요:`);
        if (!reason) return;
        setBusy(h.id);
        try {
            await httpsCallable(fns, 'adminRejectClaim')({ hospitalId: h.id, reason });
            onDone();
        } catch (e) { alert(e.message); }
        setBusy(null);
    };

    if (!items.length) {
        return <div style={S.empty}><CheckCircle2 size={20} color="#10b981" /> 검증 대기 항목 없음</div>;
    }

    return (
        <div style={S.list}>
            {items.map(h => (
                <div key={h.id} style={S.card}>
                    <div style={{ flex: 1 }}>
                        <div style={S.cardTitle}>
                            <Building2 size={16} /> <strong>{h.name}</strong>
                            {typeof h.matchScore === 'number' && (
                                <span style={h.lowScore === true ? { ...S.scoreBadge, background: '#fee2e2', color: '#b91c1c' } : S.scoreBadge}>매칭 {h.matchScore}%</span>
                            )}
                        </div>
                        {h.lowScore === true && (
                            <div style={{ fontSize: 12, color: '#b91c1c', margin: '4px 0' }}>
                                저점수 신청입니다. 치과 이름·주소·전화가 신청자 정보와 맞는지 직접 확인한 뒤 승인하세요.
                            </div>
                        )}
                        <div style={S.metaRow}><MapPin size={11} /> {h.address || '주소 없음'}</div>
                        <div style={S.metaRow}><Phone size={11} /> {h.phone || '-'}</div>
                        <div style={S.divider} />
                        <div style={S.userBox}>
                            <strong>신청자</strong>
                            <div style={S.metaRow}><User size={11} /> {h.user?.representativeName || h.user?.businessName || '-'} ({h.user?.email || h.claimedBy})</div>
                            <div style={S.metaRow}><Hash size={11} /> 사업자번호: {h.user?.businessNumber || '-'}</div>
                            <div style={S.metaRow}><MapPin size={11} /> 등록 주소: {h.user?.address || '-'}</div>
                            <div style={S.metaRow}><Phone size={11} /> {h.user?.phone || '-'}</div>
                        </div>
                    </div>
                    <div style={S.actions}>
                        <button
                            onClick={() => approve(h)}
                            disabled={busy === h.id}
                            style={S.approveBtn}
                        >
                            <CheckCircle2 size={14} /> 승인
                        </button>
                        <button
                            onClick={() => reject(h)}
                            disabled={busy === h.id}
                            style={S.rejectBtn}
                        >
                            <XCircle size={14} /> 거부
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}

/* ============== Tab: 신규 요청 ============== */
function RequestsTab({ items, onDone }) {
    const [active, setActive] = useState(null);
    const [busy, setBusy] = useState(null);
    const fns = getFunctions(undefined, 'asia-northeast3');

    const reject = async (r) => {
        const reason = window.prompt('거부 사유를 입력하세요:');
        if (!reason) return;
        setBusy(r.id);
        try {
            await httpsCallable(fns, 'adminRejectClaimRequest')({ requestId: r.id, reason });
            onDone();
        } catch (e) { alert(e.message); }
        setBusy(null);
    };

    if (!items.length) {
        return <div style={S.empty}><CheckCircle2 size={20} color="#10b981" /> 신규 요청 없음</div>;
    }

    return (
        <div style={S.list}>
            {items.map(r => (
                <div key={r.id} style={S.card}>
                    <div style={{ flex: 1 }}>
                        <div style={S.cardTitle}>
                            <Building2 size={16} /> <strong>{r.businessName}</strong>
                            <span style={S.tagBadge}>HIRA 미등록 요청</span>
                        </div>
                        <div style={S.metaRow}><MapPin size={11} /> {r.address || '-'}</div>
                        <div style={S.metaRow}><Phone size={11} /> {r.phone || '-'}</div>
                        <div style={S.metaRow}><Hash size={11} /> 사업자번호: {r.businessNumber || '-'}</div>
                        <div style={S.divider} />
                        <div style={S.userBox}>
                            <strong>신청자</strong>
                            <div style={S.metaRow}><User size={11} /> {r.user?.email || r.requestedBy}</div>
                        </div>
                    </div>
                    <div style={S.actions}>
                        <button onClick={() => setActive(r)} style={S.searchBtn}>
                            <Search size={14} /> 치과 검색·할당
                        </button>
                        <button onClick={() => reject(r)} disabled={busy === r.id} style={S.rejectBtn}>
                            <XCircle size={14} /> 거부
                        </button>
                    </div>
                </div>
            ))}
            {active && (
                <AssignModal
                    request={active}
                    onClose={() => setActive(null)}
                    onDone={() => { setActive(null); onDone(); }}
                />
            )}
        </div>
    );
}

/* ============== Tab: 분쟁 ============== */
function DisputesTab({ items, onDone }) {
    const [busy, setBusy] = useState(null);
    const fns = getFunctions(undefined, 'asia-northeast3');

    const resolve = async (d, resolution) => {
        const note = window.prompt(`처리 메모(선택):`);
        setBusy(d.id);
        try {
            await httpsCallable(fns, 'adminResolveDispute')({ disputeId: d.id, resolution, note });
            onDone();
        } catch (e) { alert(e.message); }
        setBusy(null);
    };

    if (!items.length) {
        return <div style={S.empty}><CheckCircle2 size={20} color="#10b981" /> 분쟁 없음</div>;
    }

    return (
        <div style={S.list}>
            {items.map(d => (
                <div key={d.id} style={S.card}>
                    <div style={{ flex: 1 }}>
                        <div style={S.cardTitle}>
                            <AlertTriangle size={16} color="#ef4444" />
                            <strong>{d.hospital?.name || '(삭제됨)'}</strong>
                        </div>
                        <div style={{
                            padding: 10, marginTop: 8, background: '#fef2f2',
                            borderRadius: 6, fontSize: 12, color: '#991b1b', lineHeight: 1.5,
                        }}>
                            <strong>신고 사유:</strong><br />
                            {d.reason || '(사유 없음)'}
                        </div>
                        <div style={S.divider} />
                        <div style={S.userBox}>
                            <strong>신고자</strong>
                            <div style={S.metaRow}><User size={11} /> {d.user?.email || d.reportedBy}</div>
                        </div>
                    </div>
                    <div style={S.actions}>
                        <button onClick={() => resolve(d, 'accepted')} disabled={busy === d.id} style={S.approveBtn}>
                            <CheckCircle2 size={14} /> 신고 인정
                        </button>
                        <button onClick={() => resolve(d, 'rejected')} disabled={busy === d.id} style={S.rejectBtn}>
                            <XCircle size={14} /> 기각
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}

/* ============== Modal: 치과 검색·할당 ============== */
function AssignModal({ request, onClose, onDone }) {
    const [search, setSearch] = useState(request.businessName || '');
    const [results, setResults] = useState([]);
    const [busy, setBusy] = useState(false);
    const [searching, setSearching] = useState(false);
    const fns = getFunctions(undefined, 'asia-northeast3');

    const doSearch = async () => {
        if (!search.trim()) return;
        setSearching(true);
        try {
            const text = search.trim();
            const snap = await getDocs(query(
                collection(db, 'hospitals'),
                where('isActive', '==', true),
                where('name', '>=', text),
                where('name', '<=', text + ''),
                limit(30)
            ));
            setResults(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
            alert('검색 실패: ' + e.message);
        }
        setSearching(false);
    };

    useEffect(() => { doSearch(); /* eslint-disable-next-line */ }, []);

    const assign = async (h) => {
        if (h.claimedBy && h.claimedBy !== request.requestedBy) {
            alert('이미 다른 사용자가 등록한 치과입니다');
            return;
        }
        if (!window.confirm(`${h.name} 을(를) ${request.user?.email || request.requestedBy} 에게 할당할까요?`)) return;
        setBusy(true);
        try {
            await httpsCallable(fns, 'adminAssignClaim')({
                hospitalId: h.id,
                targetUid: request.requestedBy,
                requestId: request.id,
            });
            onDone();
        } catch (e) {
            alert(e.message);
        }
        setBusy(false);
    };

    return (
        <div style={S.overlay} onClick={onClose}>
            <div style={S.modal} onClick={(e) => e.stopPropagation()}>
                <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>
                    치과 검색·할당 — <span style={{ color: '#0ea5e9' }}>{request.businessName}</span>
                </h3>
                <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px' }}>
                    신청자: {request.user?.email} · 주소: {request.address || '-'}
                </p>
                <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && doSearch()}
                        style={S.searchInput}
                        placeholder="치과명"
                    />
                    <button onClick={doSearch} disabled={searching} style={S.searchBtnSmall}>
                        {searching ? <Loader size={12} /> : <Search size={12} />} 검색
                    </button>
                </div>
                <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                    {results.length === 0 ? (
                        <div style={S.empty}>검색 결과 없음</div>
                    ) : results.map(h => (
                        <div key={h.id} style={S.resultRow}>
                            <div style={{ flex: 1 }}>
                                <strong>{h.name}</strong>
                                {h.claimedBy && (
                                    <span style={{
                                        marginLeft: 6, padding: '2px 6px',
                                        background: '#fef3c7', color: '#92400e',
                                        borderRadius: 3, fontSize: 9, fontWeight: 800,
                                    }}>
                                        이미 등록됨
                                    </span>
                                )}
                                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                                    📍 {h.address || '-'} · ☎ {h.phone || '-'}
                                </div>
                            </div>
                            <button
                                onClick={() => assign(h)}
                                disabled={busy || (h.claimedBy && h.claimedBy !== request.requestedBy)}
                                style={S.assignBtn}
                            >
                                할당
                            </button>
                        </div>
                    ))}
                </div>
                <div style={{ marginTop: 12, textAlign: 'right' }}>
                    <button onClick={onClose} style={S.cancelBtn}>닫기</button>
                </div>
            </div>
        </div>
    );
}

/* ============== UI Helpers ============== */
const TabBtn = ({ active, onClick, children }) => (
    <button onClick={onClick} style={{
        padding: '8px 14px',
        background: active ? '#0ea5e9' : '#fff',
        color: active ? '#fff' : '#475569',
        border: '1px solid ' + (active ? '#0ea5e9' : '#cbd5e1'),
        borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
    }}>{children}</button>
);

const S = {
    page: { maxWidth: 1100, margin: '0 auto', padding: 24 },
    header: { marginBottom: 16 },
    title: { fontSize: 22, fontWeight: 900, margin: 0, color: '#0f172a' },
    subtitle: { fontSize: 13, color: '#64748b', marginTop: 6 },
    tabs: { display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' },
    refreshBtn: {
        marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '6px 10px', background: '#fff', border: '1px solid #cbd5e1',
        borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', color: '#64748b',
    },
    list: { display: 'flex', flexDirection: 'column', gap: 10 },
    card: {
        display: 'flex', gap: 12,
        padding: 14, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
    },
    cardTitle: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, marginBottom: 6 },
    metaRow: { fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 },
    divider: { height: 1, background: '#e2e8f0', margin: '10px 0' },
    userBox: {
        padding: 8, background: '#f8fafc', borderRadius: 6, fontSize: 12,
    },
    actions: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 130 },
    approveBtn: {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
        padding: '8px 12px', background: '#10b981', color: '#fff',
        border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
    },
    rejectBtn: {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
        padding: '8px 12px', background: '#fff', color: '#ef4444',
        border: '1px solid #ef4444', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
    },
    searchBtn: {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
        padding: '8px 12px', background: '#0ea5e9', color: '#fff',
        border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
    },
    scoreBadge: {
        padding: '2px 8px', background: '#dbeafe', color: '#1e40af',
        borderRadius: 4, fontSize: 10, fontWeight: 800,
    },
    tagBadge: {
        padding: '2px 8px', background: '#fef3c7', color: '#92400e',
        borderRadius: 4, fontSize: 10, fontWeight: 800,
    },
    empty: {
        padding: 30, textAlign: 'center', background: '#f8fafc',
        borderRadius: 8, fontSize: 13, color: '#64748b',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    },
    error: {
        padding: 12, marginBottom: 12, background: '#fee2e2', color: '#991b1b',
        borderRadius: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
    },
    overlay: {
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    },
    modal: {
        background: '#fff', borderRadius: 12, width: '100%', maxWidth: 720, padding: 20,
        maxHeight: '90vh', overflowY: 'auto',
    },
    searchInput: {
        flex: 1, padding: '8px 10px', border: '1px solid #cbd5e1',
        borderRadius: 6, fontSize: 13,
    },
    searchBtnSmall: {
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '8px 12px', background: '#0ea5e9', color: '#fff',
        border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
    },
    resultRow: {
        display: 'flex', alignItems: 'center', gap: 8,
        padding: 10, background: '#f8fafc', border: '1px solid #e2e8f0',
        borderRadius: 6, marginBottom: 6,
    },
    assignBtn: {
        padding: '6px 10px', background: '#10b981', color: '#fff',
        border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
    },
    cancelBtn: {
        padding: '6px 12px', background: '#fff', color: '#64748b',
        border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
    },
};

export default AdminHospitalClaimReview;
